// models
import type {
  ReqApproval,
  ReqApprovalList,
  ReqChangeId,
  ReqChangePassword,
  ReqLogin,
  ReqReject,
  ReqSignIn,
  ReqSignUp,
  ReqSms,
  ResChangePassword,
  ResLogin,
  ResSignIn,
  ResSignUp,
  ResSms,
  UserApprovalInfo
} from '../models/auth.model';
import type { BaseApiResponse } from 'modules';

// services
import AxiosInstanceCreator from 'services/api';

// libraries
import qs from 'qs';
import type { AxiosInstance } from 'axios';

const authInstance = new AxiosInstanceCreator().create();

export const authApi = {
  /**
   * SMS 모바일+국가 변경 문자발송
   * @param {nationId, mobile} data
   * @returns
   */
  async getSmsChangeMobile(data: any) {
    return await authInstance.get(`/sms/change/pwd?${qs.stringify(data)}`).then((res) => res.data);
  },

  /**
   * SMS 문자발송
   * @param {nationId, mobile, status} data
   * @returns
   */
  async getSms(data: ReqSms): Promise<ResSms> {
    return await authInstance.get<ResSms>(`/sms?${qs.stringify(data)}`).then((res) => res.data);
  },

  /**
   * SMS 검증
   * @param {nationId, mobile, code, status} data
   * @returns
   */
  async getSmsVerify(data: any) {
    return await authInstance.get(`/sms/verify?${qs.stringify(data)}`).then((res) => res.data);
  },

  /**
   * SMS 검증  Pwd 변경
   * @param {nationId, mobile, code, newPassword} data
   * @returns
   */
  async getSmsVerifyForgot(data: any) {
    return await authInstance
      .get(`/sms/verify/forgot?${qs.stringify(data)}`)
      .then((res) => res.data);
  },

  /**
   * 로그인
   *
   * @param {ReqSignIn} data
   * @returns {Promise<ResSignIn>}
   */
  async postSignIn(data: ReqSignIn): Promise<ResSignIn> {
    return await authInstance.post<ResSignIn>(`/auth/login`, data).then((res) => res.data);
  },

  /**
   * 회원가입
   * @param {username, password, roles, mobile, preferLang, nationalId, companyId, designationId, birthDate, iqamaNo, bloodType, agreement} data
   * @returns
   */

  async fetchSignUp(data: any) {
    return await authInstance.post(`/auth`, JSON.stringify(data)).then((res) => res.data);
  },

  /**
   * 토큰 재발급, api.js 공통 로직으로 뺌
   * TODO: 나중에 header 넣는 방식 로직구현해야함
   * @param {*} data
   * @returns
   */
  async getToken(data: any) {
    return await authInstance.get('/token', data).then((res) => res.data);
  },

  /**
   * 비밀번호 변경
   * @param {userId, currentPassword,newPassword} data
   * @returns
   */
  async postPassword(data: ReqChangePassword): Promise<ResChangePassword> {
    return await authInstance.post<ResChangePassword>(`/auth/passwd`, data).then((res) => res.data);
  },

  /**
   * 관리자 승인 대기 리스트 조회
   * @param {company, keyword} data
   * @returns
   */
  async getAuth(data: any) {
    return await authInstance.get(`/auth/approval?${qs.stringify(data)}`).then((res) => res.data);
  },

  /**
   * 관리자 승인 (개별,단체)
   * @param {userId, status, mobilization} data
   * @returns
   */
  async postApproval(data: any) {
    return await authInstance.post(`/auth/approval`, JSON.stringify(data)).then((res) => res.data);
  },

  /**
   * 관리자 승인 거절 (개별,단체)
   * @param {userId, status} data
   * @returns
   */
  async postReject(data: any) {
    return await authInstance
      .post(`/auth/approval/reject`, JSON.stringify(data))
      .then((res) => res.data);
  }
};

export class AuthAPI {
  Axios: AxiosInstance;

  constructor() {
    this.Axios = new AxiosInstanceCreator({}).create();
  }

  /** 회원가입: 사용자 정보 (파일 X) API */
  async signUp(params: ReqSignUp): Promise<BaseApiResponse<ResSignUp>> {
    return await this.Axios.post<BaseApiResponse<ResSignUp>>('/auth', params).then(
      (res) => res.data
    );
  }

  /** 회원가입: 사용자 정보 + 파일 API */
  async signUpWithFile(params: {
    user: ReqSignUp;
    file: File;
  }): Promise<BaseApiResponse<ResSignUp>> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('user', JSON.stringify(params.user));

    return await this.Axios.post<BaseApiResponse<ResSignUp>>('/auth/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then((res) => res.data);
  }

  /** Password 로그인 API */
  async login(params: ReqLogin): Promise<ResLogin> {
    return await this.Axios.post<ResLogin>('/auth/login', params).then((res) => res.data);
  }

  /** userId 통한 비밀번호 변경 API */
  async changePassword(params: {
    userId: string;
    newPassword: string;
  }): Promise<BaseApiResponse<ResSignUp>> {
    return await this.Axios.post<BaseApiResponse<ResSignUp>>('/auth/passwd', params).then(
      (res) => res.data
    );
  }

  /** 아이디 변경 API */
  async changeId(data: ReqChangeId): Promise<BaseApiResponse> {
    return authInstance.put(`/users/mobile`, data).then((res) => res.data);
  }

  /** 가입 승인 관리자 페이지 조회 API */
  async getApprovalList(
    params: ReqApprovalList
  ): Promise<BaseApiResponse<unknown, UserApprovalInfo>> {
    return await this.Axios.get<BaseApiResponse<unknown, UserApprovalInfo>>('/auth/approval', {
      params
    }).then((res) => res.data);
  }

  /** 가입 승인 API */
  async approveUsers(params: ReqApproval): Promise<BaseApiResponse<string>> {
    return await this.Axios.post<BaseApiResponse<string>>('/auth/approval', params).then(
      (res) => res.data
    );
  }

  /** 가입 거절 API */
  async rejectUsers(params: ReqReject): Promise<BaseApiResponse<string>> {
    return await this.Axios.post<BaseApiResponse<string>>('/auth/approval/reject', params).then(
      (res) => res.data
    );
  }
}
